import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, posterUrl, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/BrandingContext";
import { toast } from "sonner";
import { Play, Clock, Tv, ArrowLeft, Lock, Share2 } from "lucide-react";

export default function MovieDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const branding = useBranding();
  const [movie, setMovie] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/movies/${id}`).then(({ data }) => { setMovie(data); setLoading(false); })
      .catch(() => { setLoading(false); });
  }, [id]);

  // Per-movie OG meta sync: any JS-aware scraper (LinkedIn, some preview bots)
  // will read these. Non-JS scrapers should use the /api/og/movie/{id} share URL.
  useEffect(() => {
    if (!movie) return;
    const title = `${movie.title} — ${branding.og_title || "Christoreel"}`;
    const desc = (movie.description || `Watch ${movie.title} on Christoreel — by ${movie.channel_name || "an African creator"}.`).slice(0, 297);
    const img = movie.cloudflare_thumbnail_url || posterUrl(movie.id);
    document.title = title;
    const setMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", desc);
    setMeta("property", "og:image", img);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", desc);
    setMeta("name", "twitter:image", img);
    // Restore site branding when this page unmounts so /browse, /, etc. don't keep movie meta.
    return () => {
      if (branding.og_title) setMeta("property", "og:title", branding.og_title);
      if (branding.og_description) setMeta("property", "og:description", branding.og_description);
      if (branding.og_image_url) setMeta("property", "og:image", branding.og_image_url);
      if (branding.site_title) document.title = branding.site_title;
    };
  }, [movie, branding.og_title, branding.og_description, branding.og_image_url, branding.site_title]);

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-[#E65F2B] border-t-transparent animate-spin" /></div>;
  if (!movie) return <div className="max-w-[1400px] mx-auto px-6 py-20" data-testid="movie-not-found">Movie not found.</div>;

  const authed = user && user !== false;
  const subscribed = authed && (user.subscription_status === "active" || user.role === "admin" || movie.creator_id === user._id);
  const canPlay = authed && subscribed && movie.status === "approved";

  const handlePlay = () => {
    if (!authed) return nav("/login");
    if (!subscribed) return nav("/subscription");
    nav(`/watch/${movie.id}`);
  };

  const shareUrl = `${BACKEND_URL}/api/og/movie/${movie.id}`;
  const handleShare = async () => {
    const data = { title: movie.title, text: `Watch "${movie.title}" on Christoreel`, url: shareUrl };
    try {
      if (navigator.share) {
        await navigator.share(data);
        return;
      }
    } catch { /* user cancelled */ }
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Share link copied — paste it anywhere for a rich preview.");
    } catch {
      toast.error("Couldn't copy link. Long-press to copy manually.");
    }
  };

  return (
    <div data-testid="movie-detail-page">
      <div className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <img src={posterUrl(movie.id)} alt="" className="absolute inset-0 h-full w-full object-cover" onError={(e) => e.currentTarget.style.display = "none"} />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12 h-full flex flex-col justify-end pb-12">
          <Link to="/browse" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white mb-6 w-fit" data-testid="back-to-browse">
            <ArrowLeft className="h-4 w-4" /> Back
          </Link>
          <p className="text-eyebrow text-[#E65F2B] mb-2">{(movie.genres && movie.genres.length ? movie.genres : [movie.genre]).filter(Boolean).join(" · ")}</p>
          <h1 className="font-display text-5xl lg:text-7xl font-black tracking-tighter max-w-3xl" data-testid="movie-title">{movie.title}</h1>
          <div className="flex flex-wrap items-center gap-6 mt-4 text-sm text-white/60">
            <span className="flex items-center gap-2"><Tv className="h-4 w-4" /> {movie.channel_name}</span>
            {movie.duration_seconds > 0 && (
              <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {Math.round(movie.duration_seconds / 60)} min</span>
            )}
            <span className="badge-pill bg-white/5 border border-white/10">{movie.status === "approved" ? "Available" : "Pending Review"}</span>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12 grid grid-cols-12 gap-12">
        <div className="col-span-12 lg:col-span-8">
          <h2 className="font-display text-2xl font-bold mb-4">About this title</h2>
          <p className="text-white/70 leading-relaxed whitespace-pre-wrap" data-testid="movie-description">{movie.description || "No description provided."}</p>

          <div className="flex flex-wrap gap-4 mt-10">
            <button
              onClick={handlePlay}
              data-testid="play-movie-button"
              disabled={movie.status !== "approved" && user?.role !== "admin" && movie.creator_id !== user?._id}
              className="ember-btn text-base disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {canPlay ? <><Play className="h-5 w-5 fill-white" /> Play Now</> : <><Lock className="h-4 w-4" /> {authed ? "Subscribe to watch" : "Sign in to watch"}</>}
            </button>
            <Link to="/browse" className="ghost-btn">More titles</Link>
            <button
              onClick={handleShare}
              data-testid="share-movie-button"
              className="ghost-btn"
              title="Copy a rich social-preview share link"
            >
              <Share2 className="h-4 w-4" /> Share
            </button>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-4">
          <div className="card-surface p-6">
            <p className="text-eyebrow mb-3">Channel</p>
            <p className="font-display text-xl font-bold">{movie.channel_name}</p>
            <p className="text-xs text-white/40 mt-1">Approved creator</p>
            <div className="mt-6 pt-6 border-t border-white/5 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-white/40">Views</p>
                <p className="font-display font-bold mt-1">{movie.views?.toLocaleString() || 0}</p>
              </div>
              <div>
                <p className="text-xs text-white/40">Released</p>
                <p className="font-display font-bold mt-1">{new Date(movie.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
